import React from 'react';
import cn from 'classnames';
import {ArrowDropDown} from '@material-ui/icons';
import {
  ColumnTablePropMap,
  ColumnTableProps,
  RowTableProps,
  StudioTableSortProps,
} from '.';

import TableCell, {
  TableCellProps as MUITableCellProps,
} from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import {Paper, PaperProps, TableSortLabel} from '@material-ui/core';

const HEADER_SIZE = 50;
export const ROW_SIZE = 35;

/**
 * Renders the headers row based on the columns provided.
 */
export type TableHeadersProps = {
  classes: any;
  height?: number;
  columns: ColumnTableProps[];
} & StudioTableSortProps;

export type TableCellProps = {
  field: string;
  rowSize?: number;
  className?: string;
};

export const TableHeaderCell = ({
  column,
  colIndex,
  classes,
  height = HEADER_SIZE,
  sort,
  sortDir,
  onSort = () => {},
}: {column: ColumnTableProps; colIndex: number} & TableHeadersProps) => (
  <TableCell
    key={colIndex}
    // component="div"
    variant="head"
    align={column.numeric || false ? 'right' : 'left'}
    className={cn(classes.cell, classes.column, !column.width && classes.expandingCell)}
    style={{
      flexBasis: column.width || undefined,
      height: height,
    }}
    scope="col"
  >
    {column.sortable ? (
      <TableSortLabel
        active={sort === column.id}
        direction={sort === column.id ? sortDir : 'asc'}
        onClick={() => onSort(column.id)}
        IconComponent={ArrowDropDown}
      >
        {column.label}
      </TableSortLabel>
    ) : (
      <span>{column.label}</span>
    )}
  </TableCell>
);

export const TableHeaders = ({classes, columns, ...rest}: TableHeadersProps) => (
  <TableRow className={cn(classes.row, classes.headerRow)}>
    {columns.map((column, colIndex) => {
      return (
        <TableHeaderCell
          column={column}
          colIndex={colIndex}
          classes={classes}
          columns={columns}
          {...rest}
        />
      );
    })}
  </TableRow>
);

type StudioVirtualizedTableRowContextType = {
  index: number;
  item: any;
  columnDef: ColumnTableProps[];
  columnDefObj: ColumnTablePropMap;
  onClickCell?: (colIndex: number, rowIndex: number, item: any) => void;
  onClickRow?: (index: number, item: any) => void;
};

const StudioVirtualizedTableRowContext = React.createContext<
  StudioVirtualizedTableRowContextType | undefined
>(undefined);

export const useStudioVirtualizedTableRow = () => {
  const context = React.useContext(StudioVirtualizedTableRowContext);
  return context!;
};

export const StudioVirtualizedTableCell = ({children, ...rest}: MUITableCellProps) => (
  <TableCell {...rest}>{children}</TableCell>
);

export const StudioTableFooter = ({children, ...rest}: PaperProps) => (
  <Paper className="virtualized-table__footer" elevation={4} {...rest}>
    {children}
  </Paper>
);

// Numeric table are normally right aligned
export const StudioVirtualizedTableNumericCell = ({
  children,
  ...rest
}: MUITableCellProps) => (
  <TableCell align="right" {...rest}>
    {children}
  </TableCell>
);

/**
 * Default Row component
 *  Used if column renderer is not given. It will used the config found on the header definition
 */
export const DefaultRow = ({
  index,
  style,
  data: {columnDef, items, classes},
  onClick = () => {},
}: RowTableProps) => {
  const item = items[index];
  const onRowClick = () => onClick(item);

  return (
    <TableRow className={classes.row} style={style} onClick={onRowClick}>
      {columnDef.map((column, colIndex) => {
        return (
          <TableCell
            key={item.id + colIndex}
            align={column.numeric || false ? 'right' : 'left'}
            className={cn(classes.cell, !column.width && classes.expandingCell)}
            style={{
              flexBasis: column.width || undefined,
              height: ROW_SIZE,
            }}
          >
            {item[column.id]}
          </TableCell>
        );
      })}
    </TableRow>
  );
};

/**
 * Custom Row component
 * Used when the children property is given and wraps the Context Provider on each row so the row data is accessible via useStudioVirtualizedTableRow hook. This gives the Cell more flexibility on custom rendering.
 */
export const WrappedRow = ({
  index,
  style,
  onClick = () => {},
  data: {columnDef, columns, items, classes, columnDefObj},
}: RowTableProps) => {
  const item = items[index];
  const onRowClick = () => onClick(item);

  return (
    <StudioVirtualizedTableRowContext.Provider
      key={index}
      value={{index, item, columnDef, columnDefObj}}
    >
      <TableRow key={index} className={classes.row} style={style} onClick={onRowClick}>
        {columns}
      </TableRow>
    </StudioVirtualizedTableRowContext.Provider>
  );
};
